import { useMemo } from "react";

import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import MetaInfoListItem from "components/MetaInfoList/Item";
import SortableTable from "components/SortableTable";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryOrderDirection } from "types/generated";
import { getCommitDigitsCount } from "utils/commit";
import { ModuleContext } from "views/Account/Module/Context";
import RunStateBadge from "components/Run/elements/StateBadge";
import Timestamp from "components/Timestamp";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";

import ModuleVersionPageIntoTitle from "../../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../../components/ScrollableWrapper";
import { VersionContext } from "../../Context";
import { COLUMN_ORDER, columns } from "./constants";

const ModuleVersionTestCases = () => {
  const { version } = useTypedContext(VersionContext);
  const { module } = useTypedContext(ModuleContext);

  const testCases = useMemo(() => version?.runs || [], [version?.runs]);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Test cases"
          tooltip="Special type of runs executed on the module. Infrastructure is created for the test, and after the test finishes, it will be deleted."
        />
        {/** TODO: confirm what to do with the new Rerun button */}
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {testCases.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={EmptystateSearchNoResultsColored}
            caption="This module has no test cases"
            fullHeight
          />
        )}
        {testCases.length > 0 && (
          <SortableTable
            ariaLabel="Test cases"
            items={testCases}
            columns={columns}
            columnOrder={COLUMN_ORDER}
            stickyHeader
            renderItems={(sorted) =>
              sorted.map((item) => (
                <ListEntitiesItem
                  role="row"
                  key={item.id}
                  gap="large"
                  grid
                  gridTemplate={COLUMN_ORDER}
                >
                  <Box role="cell" align="center">
                    <RunStateBadge state={item.state} />
                  </Box>
                  <Box role="rowheader">
                    <ListEntitiesItemLink
                      title={item.expectFailure ? `${item.title} (should fail)` : item.title}
                      href={`/module/${module.id}/version/${version.id}/run/${item.id}`}
                    />
                  </Box>
                  <Box role="cell" direction="column" justify="center">
                    <MetaInfoListItem
                      href={item.commit.url}
                      linkText={item.commit.hash.slice(0, getCommitDigitsCount(module.provider))}
                      target="_blank"
                    />
                  </Box>
                  <Box role="cell" align="center">
                    <Timestamp variant="p-body2" timestamp={item.createdAt} />
                  </Box>
                </ListEntitiesItem>
              ))
            }
            initialSortBy="id"
            initialDirection={SearchQueryOrderDirection.Desc}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionTestCases;
